import { useCallback, useEffect, useRef, useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { PaginationBar } from "@/components/shared/PaginationBar";
import { usePaymentDeskRefData } from "./shared/usePaymentDeskRefData";
import LineFormDialog from "./LineFormDialog";
import { EditHistoryDialog } from "./shared/EditHistoryDialog";
import { formatIst } from "./shared/formatIst";
import { firstName, groupByFirm } from "./shared/queueGrouping";
import { formatLastPaid } from "./shared/lastPaid";
import { DownloadListImageButton } from "./shared/DownloadListImageButton";
import { money } from "./PaymentListDetail";
import { AlertTriangle, Check, ChevronDown, ChevronRight, Undo2, Pencil, History, RefreshCw } from "lucide-react";

function PayeeName({ line }) {
  return (
    <div data-testid={`payee-cell-${line.id}`}>
      <div className="text-[15px] font-normal text-slate-800" data-testid={`payee-name-${line.id}`}>
        {line.payee_name || line.payee_type}
      </div>
      {line.payee_bank_complete === false && (
        <span className="mt-0.5 flex items-center gap-1 text-[10px] text-red-600" data-testid={`payee-bank-warning-${line.id}`}>
          <AlertTriangle className="h-3 w-3" /> Bank details incomplete
        </span>
      )}
    </div>
  );
}

const POLL_MS = 60000;
const PILL = {
  Submitted: "bg-blue-500",
  "Sent Back": "bg-red-500",
  Approved: "bg-emerald-600",
};

function headerStatus(lines) {
  if (lines.some((l) => l.status === "Sent Back")) return { label: "Needs Attention", cls: "bg-red-500" };
  if (lines.length > 0 && lines.every((l) => l.status === "Approved")) return { label: "All Approved", cls: "bg-emerald-600" };
  return { label: "Pending Approval", cls: "bg-amber-500" };
}

const isActionable = (l) => l.status === "Submitted" || l.status === "Sent Back";

// Approval Queue: RTGS/NEFT lines grouped under their Payment List.
// Director approves/sends back; Back Office/Manager/Accounts read only.
// Updates are applied in place — the screen is never refetched after an action.
export default function ApprovalQueueTab() {
  const { user } = useAuth();
  const isDirector = user?.role === "Director";
  const refData = usePaymentDeskRefData();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [newCount, setNewCount] = useState(0);
  const [editTarget, setEditTarget] = useState(null);
  const [sendBackTarget, setSendBackTarget] = useState(null);
  const [sendBackReason, setSendBackReason] = useState("");
  const [historyTarget, setHistoryTarget] = useState(null);
  const baseline = useRef(null);

  const load = useCallback((pageNum = 1, reset = false) => {
    setLoading(true);
    client
      .get("/payment-desk/approval-queue", { params: { page: pageNum, page_size: 20 } })
      .then((res) => {
        setGroups(res.data.items);
        setPage(res.data.page);
        setTotalPages(res.data.total_pages);
        setSelected({});
        setRowErrors({});
        if (reset) setExpanded({});
        if (res.data.page === 1) {
          baseline.current = res.data.items[0]?.first_submitted_at || baseline.current;
          setNewCount(0);
        }
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(1); }, [load]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!baseline.current) return;
      client
        .get("/payment-desk/approval-queue/new-lists", { params: { since: baseline.current } })
        .then((res) => setNewCount(res.data.count))
        .catch(() => {});
    }, POLL_MS);
    return () => clearInterval(timer);
  }, []);

  const patchLine = (lineId, patch) =>
    setGroups((gs) => gs.map((g) => ({ ...g, lines: g.lines.map((l) => (l.id === lineId ? { ...l, ...patch } : l)) })));

  const findLine = (lineId) => groups.flatMap((g) => g.lines).find((l) => l.id === lineId);
  const setError = (lineId, msg) => setRowErrors((e) => ({ ...e, [lineId]: msg }));
  const clearError = (lineId) => setRowErrors((e) => { const { [lineId]: _drop, ...rest } = e; return rest; });

  const approveLines = async (lineIds) => {
    const prev = Object.fromEntries(lineIds.map((id) => [id, findLine(id)]));
    setSelected((s) => { const n = { ...s }; lineIds.forEach((id) => delete n[id]); return n; });
    lineIds.forEach((id) => { clearError(id); patchLine(id, { status: "Approved" }); });
    const results = await Promise.allSettled(lineIds.map((id) => client.post(`/payment-desk/approval-queue/lines/${id}/approve`)));
    let failed = 0;
    results.forEach((r, i) => {
      const id = lineIds[i];
      const line = prev[id];
      if (r.status === "fulfilled") {
        patchLine(id, { ...r.value.data });
      } else {
        failed += 1;
        patchLine(id, { status: line.status });
        setError(id, `Line #${line.line_no}: ${getErrorMessage(r.reason)}`);
      }
    });
    if (failed === 0) toast.success(lineIds.length === 1 ? "Line approved" : `${lineIds.length} lines approved`);
    else toast.error(`${failed} line(s) could not be approved`);
  };

  const sendBack = async () => {
    const reason = sendBackReason.trim();
    if (!reason) return toast.error("A remark is required when sending a line back");
    const line = sendBackTarget;
    setSendBackTarget(null);
    setSendBackReason("");
    clearError(line.id);
    setSelected((s) => ({ ...s, [line.id]: false }));
    patchLine(line.id, { status: "Sent Back", sent_back_reason: reason });
    try {
      const res = await client.post(`/payment-desk/approval-queue/lines/${line.id}/send-back`, { reason });
      patchLine(line.id, { ...res.data });
      toast.success("Line sent back to creator");
    } catch (err) {
      patchLine(line.id, { status: line.status, sent_back_reason: line.sent_back_reason });
      setError(line.id, `Line #${line.line_no}: ${getErrorMessage(err)}`);
      toast.error("Send back failed");
    }
  };

  const refreshLine = async (lineId) => {
    try {
      const res = await client.get(`/payment-desk/lines/${lineId}`);
      patchLine(lineId, { ...res.data });
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const toggleSelect = (id, on) => setSelected((s) => ({ ...s, [id]: on }));
  const toggleAll = (lines, on) =>
    setSelected((s) => { const n = { ...s }; lines.filter(isActionable).forEach((l) => { n[l.id] = on; }); return n; });

  return (
    <div className="space-y-3" data-testid="approval-queue-tab">
      {newCount > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900" data-testid="approval-queue-new-banner">
          <span>{newCount} new Payment List{newCount > 1 ? "s" : ""} submitted.</span>
          <Button size="sm" variant="outline" onClick={() => load(1, true)} data-testid="approval-queue-show-latest">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Show latest
          </Button>
        </div>
      )}

      {loading ? (
        <div className="rounded-lg border border-slate-200 bg-white py-8 text-center text-slate-400" data-testid="approval-queue-loading">Loading...</div>
      ) : groups.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white py-8 text-center text-slate-400" data-testid="approval-queue-empty">No Payment Lists in the queue.</div>
      ) : (
        groups.map((g) => {
          const status = headerStatus(g.lines);
          const open = !!expanded[g.list_id];
          const total = g.lines.reduce((sum, l) => sum + l.amount, 0);
          const creators = [...new Set(g.lines.map((l) => l.created_by_name))];
          const actionable = g.lines.filter(isActionable);
          const picked = actionable.filter((l) => selected[l.id]);
          const allPicked = actionable.length > 0 && picked.length === actionable.length;
          return (
            <div key={g.list_id} className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`approval-group-${g.list_id}`}>
              <div className="flex items-center pr-3 hover:bg-slate-50">
              <button
                type="button"
                className="flex flex-1 flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-left"
                onClick={() => setExpanded((e) => ({ ...e, [g.list_id]: !e[g.list_id] }))}
                data-testid={`approval-group-header-${g.list_id}`}
              >
                {open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                <span className="font-mono text-sm font-semibold">{g.list_id}</span>
                <span className="text-xs text-slate-500" data-testid={`approval-group-count-${g.list_id}`}>{g.lines.length} line{g.lines.length !== 1 ? "s" : ""}</span>
                <Badge className={status.cls} data-testid={`approval-group-status-${g.list_id}`}>{status.label}</Badge>
                <span className="text-sm font-medium" data-testid={`approval-group-total-${g.list_id}`}>{money(total)}</span>
                {creators.length === 1 && (
                  <span className="text-xs text-slate-500" data-testid={`approval-group-submitter-${g.list_id}`}>
                    {firstName(creators[0])} · {formatIst(g.first_submitted_at)}
                  </span>
                )}
              </button>
              <DownloadListImageButton listId={g.list_id} />
              </div>

              {open && (
                <div className="border-t border-slate-200" data-testid={`approval-group-body-${g.list_id}`}>
                  {isDirector && picked.length > 0 && (
                    <div className="flex justify-end px-4 pt-3">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => approveLines(picked.map((l) => l.id))} data-testid={`approve-selected-${g.list_id}`}>
                        <Check className="h-3.5 w-3.5 mr-1" /> Approve Selected ({picked.length})
                      </Button>
                    </div>
                  )}
                  <Table data-testid={`approval-lines-table-${g.list_id}`}>
                    <TableHeader>
                      <TableRow>
                        {isDirector && (
                          <TableHead className="w-10">
                            <Checkbox checked={allPicked} disabled={actionable.length === 0} onCheckedChange={(v) => toggleAll(g.lines, v === true)} data-testid={`select-all-${g.list_id}`} />
                          </TableHead>
                        )}
                        <TableHead>S.No</TableHead>
                        <TableHead>Paid To</TableHead>
                        <TableHead className="min-w-[180px]">Remarks</TableHead>
                        <TableHead className="whitespace-nowrap">Last Paid</TableHead>
                        <TableHead className="min-w-[140px]">Site</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {groupByFirm(g.lines).flatMap((firm) => {
                        const colSpan = isDirector ? 9 : 8;
                        const firmRows = [
                          <TableRow key={`firm-${firm.name}`} className="bg-slate-100/70 hover:bg-slate-100/70" data-testid={`approval-firm-header-${g.list_id}-${firm.name}`}>
                            <TableCell colSpan={colSpan} className="py-2">
                              <div className="flex flex-wrap items-center gap-x-4 text-lg font-bold uppercase tracking-wide text-slate-800">
                                <span data-testid={`approval-firm-name-${g.list_id}-${firm.name}`}>{firm.name}</span>
                                <span className="text-base font-normal normal-case text-slate-500" data-testid={`approval-firm-count-${g.list_id}-${firm.name}`}>{firm.items.length} line{firm.items.length !== 1 ? "s" : ""}</span>
                              </div>
                            </TableCell>
                          </TableRow>,
                        ];
                        const lineRows = firm.items.flatMap((line) => {
                        const creatorRemark = (line.purpose || "").trim();
                        const showRemark = line.status === "Sent Back" && line.sent_back_reason;
                        const err = rowErrors[line.id];
                        const hasNote = showRemark || err;
                        const rows = [
                          <TableRow key={line.id} data-testid={`approval-line-row-${line.id}`} className={hasNote ? "border-b-0" : ""}>
                            {isDirector && (
                              <TableCell>
                                {isActionable(line) && (
                                  <Checkbox checked={!!selected[line.id]} onCheckedChange={(v) => toggleSelect(line.id, v === true)} data-testid={`select-line-${line.id}`} />
                                )}
                              </TableCell>
                            )}
                            <TableCell className="text-sm">{line.line_no}</TableCell>
                            <TableCell><PayeeName line={line} /></TableCell>
                            <TableCell className="text-[15px] font-normal text-slate-800" data-testid={`approval-line-remarks-${line.id}`}>{creatorRemark || "—"}</TableCell>
                            <TableCell className="whitespace-nowrap text-xs text-slate-500" data-testid={`approval-line-last-paid-${line.id}`}>{formatLastPaid(line.last_paid)}</TableCell>
                            <TableCell className="text-xs text-slate-500">{line.site_label || line.equipment_label || "—"}</TableCell>
                            <TableCell>{money(line.amount)}</TableCell>
                            <TableCell><Badge className={PILL[line.status]} data-testid={`approval-line-status-${line.id}`}>{line.status}</Badge></TableCell>
                            <TableCell className="text-right space-x-1.5 whitespace-nowrap">
                              {(line.edit_history || []).length > 0 && (
                                <Button size="sm" variant="outline" data-testid={`approval-line-history-${line.id}`} onClick={() => setHistoryTarget(line)}>
                                  <History className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              {isDirector && line.status === "Submitted" && (
                                <>
                                  <Button size="sm" variant="outline" data-testid={`approval-line-edit-${line.id}`} onClick={() => setEditTarget(line)}>
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button size="sm" variant="destructive" data-testid={`approval-line-send-back-${line.id}`} onClick={() => { setSendBackTarget(line); setSendBackReason(""); }}>
                                    <Undo2 className="h-3.5 w-3.5 mr-1" /> Send Back
                                  </Button>
                                </>
                              )}
                            </TableCell>
                          </TableRow>,
                        ];
                        if (hasNote) {
                          rows.push(
                            <TableRow key={`${line.id}-note`} data-testid={`approval-line-note-${line.id}`}>
                              <TableCell colSpan={colSpan} className="pt-0 pb-2 space-y-0.5">
                                {showRemark && <p className="text-xs italic text-slate-600" data-testid={`approval-line-remark-${line.id}`}>Send Back remark: {line.sent_back_reason}</p>}
                                {err && <p className="text-xs text-red-600" data-testid={`approval-line-error-${line.id}`}>{err}</p>}
                              </TableCell>
                            </TableRow>
                          );
                        }
                        return rows;
                        });
                        const totalRow = (
                          <TableRow key={`firm-total-${firm.name}`} className="bg-slate-50 hover:bg-slate-50" data-testid={`approval-firm-total-row-${g.list_id}-${firm.name}`}>
                            <TableCell colSpan={isDirector ? 6 : 5} className="py-2 text-right text-lg font-bold text-slate-800">Total</TableCell>
                            <TableCell className="py-2 text-lg font-bold text-slate-800" data-testid={`approval-firm-subtotal-${g.list_id}-${firm.name}`}>{money(firm.total)}</TableCell>
                            <TableCell colSpan={2} className="py-2" />
                          </TableRow>
                        );
                        return [...firmRows, ...lineRows, totalRow];
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          );
        })
      )}
      <PaginationBar page={page} totalPages={totalPages} onPageChange={(p) => load(p)} testId="approval-queue-pagination" />

      {editTarget && (
        <LineFormDialog
          line={editTarget}
          refData={refData}
          directorMode
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            const id = editTarget.id;
            setEditTarget(null);
            refreshLine(id);
          }}
        />
      )}
      {historyTarget && <EditHistoryDialog line={historyTarget} onClose={() => setHistoryTarget(null)} />}

      {sendBackTarget && (
        <Dialog open onOpenChange={(open) => !open && setSendBackTarget(null)}>
          <DialogContent className="max-w-md" data-testid="send-back-dialog">
            <DialogHeader>
              <DialogTitle>Send Back — {sendBackTarget.list_id} · Line #{sendBackTarget.line_no}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2">
              <p className="text-xs text-slate-500">The line returns to the creator as an editable draft. A remark is required.</p>
              <Textarea data-testid="send-back-reason-input" placeholder="Why is this line being sent back?" value={sendBackReason} onChange={(e) => setSendBackReason(e.target.value)} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSendBackTarget(null)} data-testid="send-back-cancel-button">Cancel</Button>
              <Button variant="destructive" onClick={sendBack} data-testid="send-back-confirm-button">Send Back</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
